import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { CustomValidatorService } from '@klickdata/core/form/src/validators/custom-validator.service';
import { MessageSavedComponent, MessageService } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceOpportunityService } from '@klickdata/core/opportunity';
import {
    AppScope,
    OccasionsDatesData,
    OccasionsDatesService,
    Resource,
    ResourceService,
} from '@klickdata/core/resource';
import { OccasionDate, PlayableResourceSpec, ResourceData } from '@klickdata/core/resource/src/resource.model';
import { ResourceActionHandlerService } from '@klickdata/core/resource/src/resource-action-handler.service';
import { StartResourceService } from '@klickdata/core/resource/src/start-resource.service';
import { ResourceTypes } from '@klickdata/core/resource/src/types.enum';
import {
    Filter,
    GlobalFilterProperty,
    GlobalFilterPropertyType,
    SelectFilterOption,
    StatusFilterOption,
    TableFilterComponent,
} from '@klickdata/core/table';
import { User, UserActionHistory } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { ResourceTableBaseComponent } from '@klickdata/shared-components/src/resource-table-base/resource-table-base.component';
import { ShareService } from '@klickdata/shared-components/src/share/share.service';
import { DownloadHelperService } from 'apps/klickdata/src/app/shared/dialog/download-pdf-dialog/download-helper.service';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import moment from 'moment';
import { LocalStorage } from 'ngx-webstorage';
import { BehaviorSubject, EMPTY, filter, Observable, switchMap, takeUntil, tap } from 'rxjs';
import { FormBuilder } from '@angular/forms';

@Component({
    selector: 'app-dashboard-user-occasion-activity-list',
    templateUrl: './dashboard-user-occasion-activity-list.component.html',
    styleUrls: ['./dashboard-user-occasion-activity-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardUserOccasionActivityListComponent extends OnDestroyHandler implements AfterViewInit, OnInit {
    @ViewChild('tableBase') tableBase: ResourceTableBaseComponent;
    private _tableFilterItems: Filter<any>[];
    @Input() set tableFilterItems(filters: Filter<any>[]) {
        this._tableFilterItems = [
            ...filters,
            new Filter('eager', ['user_collaboration', 'user_enrollment', 'scorm', 'user_competence']),
        ];
    }
    get tableFilterItems(): Filter<any>[] {
        return this._tableFilterItems;
    }
    @Input() user: User;
    @Input() pageSizeOptions = [25, 50, 100];
    @Input() matSortActive = 'last_action';
    @ViewChild('title', { static: true }) title: TemplateRef<any>;
    @ViewChild('img', { static: true }) img: TemplateRef<any>;
    @ViewChild('type', { static: true }) type: TemplateRef<any>;
    @ViewChild('enrollment_period', { static: false }) enrollment_period: TemplateRef<any>;
    @ViewChild('occasion_period', { static: false }) occasion_period: TemplateRef<any>;
    @ViewChild('enrollmentCount', { static: false }) enrollmentCount: TemplateRef<any>;
    @ViewChild('expansionContextTemplate', { static: true }) expansionContextTemplate: TemplateRef<any>;
    @ViewChild('multipleSelectionActionsTemplate', { static: true }) multipleSelectionActionsTemplate: TemplateRef<any>;
    // @ViewChild('multipleSelectionActions', { static: true }) multipleSelectionActions: TemplateRef<any>;
    @ViewChild('tools', { static: true }) tools: TemplateRef<any>;
    @ViewChild('occasionsManager', { static: true }) occasionsManager: TemplateRef<any>;
    public resourceStatusOptions: StatusFilterOption[];
    public resourceTypeOptions: SelectFilterOption[];
    public shareLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @ViewChild(TableFilterComponent) filter: TableFilterComponent;
    GlobalFilterPropertyType = GlobalFilterPropertyType;
    public isMobile$: Observable<boolean>;
    public customer$: Observable<Customer>;
    @LocalStorage(UserActionHistory.SELECTED_HOME_TABS_RESOURCE_ID, null)
    public expandedRowId: number | null;
    public expandedRowIds: Set<number> | null = null;
    GlobalFilterProperty = GlobalFilterProperty;
    columnsData: { [key: string]: TemplateRef<any> } = {};
    otherContext: { [key: string]: TemplateRef<any> } = {};
    columnsKeys: any = {
        img: { label: $localize`Image`, sortable: false },
        title: { label: $localize`Title`, sortable: true },
    };
    ResourceTypes = ResourceTypes;
    AppScope = AppScope;
    private latestOccasionFormState: { valid: boolean; value?: OccasionsDatesData } | null = null;
    public dataDownloads: any;
    get isAllExpanded(): boolean {
        if (!this.expandedRowIds || !this.tableBase?.dataSource?.data?.length) {
            return false;
        }
        const expandableIds = this.tableBase.dataSource.data
            .filter((r) => this.canExpand(r))
            .map((r) => r.opportunity_id ?? r.id);
        return expandableIds.length > 0 && expandableIds.every((id) => this.expandedRowIds?.has(id));
    }

    expandedFn = (resource: Resource) => {
        const id = resource.opportunity_id || resource.id;
        return this.expandedRowIds ? this.expandedRowIds.has(id) : id === this.expandedRowId;
    };

    onOccasionFormChange(state: { valid: boolean; value?: OccasionsDatesData }): void {
        let data: any = {};
        if (state.value) {
            data = {
                ...state.value,
                total_seats: state.value.unlimited_seats ? null : state.value.total_seats,
            };
            delete data.unlimited_seats;
            delete data.id;
            delete data.expanded;
        }
        this.latestOccasionFormState = {
            valid: state.valid,
            value: data,
        };
    }

    constructor(
        protected downloadHelper: DownloadHelperService,
        public shareService: ShareService,
        protected router: Router,
        protected route: ActivatedRoute,
        protected cdr: ChangeDetectorRef,
        protected resourceOpportunityService: ResourceOpportunityService,
        protected mobile: MobileService,
        protected auth: AuthService,
        protected oppoService: ResourceOpportunityService,
        protected occasionsDatesService: OccasionsDatesService,
        protected messageService: MessageService,
        protected resourceActionHandlerService: ResourceActionHandlerService,
        protected fb: FormBuilder,
        protected validatorService: CustomValidatorService
    ) {
        super();
        this.resourceStatusOptions = Utils.listResourceOccasionsStatus();
        this.resourceTypeOptions = Utils.getResourceTypeOptions();
        this.isMobile$ = mobile.isMobile();
    }
    ngOnInit(): void {
        this.route.queryParams
            .pipe(
                filter((params) => params.expand),
                takeUntil(this.destroy)
            )
            .subscribe((params) => {
                setTimeout(() => {
                    this.expandedRowId = +params.expand;
                    this.cdr.markForCheck();
                }, 500);
            });
        this.customer$ = this.auth.getCustomer();
    }
    ngAfterViewInit(): void {
        this.columnsData['img'] = this.img;
        this.columnsData['title'] = this.title;
        this.columnsData['tools'] = this.tools;
        this.otherContext['expansionContext'] = this.expansionContextTemplate;
        this.otherContext['multipleSelectionActions'] = this.multipleSelectionActionsTemplate;
        this.columnsKeys['occasion_period'] = { label: $localize`Occasion Period`, sortable: true };
        this.columnsKeys['enrollmentCount'] = { label: $localize`Enrollments Count`, sortable: true };
        this.columnsData['occasion_period'] = this.occasion_period;
        this.columnsData['enrollmentCount'] = this.enrollmentCount;
        const toolsTemplate = this.columnsData['tools'];
        delete this.columnsData['tools'];
        this.columnsData['tools'] = toolsTemplate;
        this.tableFilterItems.push(new Filter('passed', [false]));
    }
    public getOccasionDates(resource: Resource): OccasionDate[] {
        const dates = resource.enrollmentRequiredMetadata?.occasion_dates;
        return !!dates?.length ? Utils.filterEmptyOccasionDates(dates).filter((item) => !item.ispassed) : [];
    }
    onChangeStatus() {
        this.tableBase.dataSource.refresh();
    }
    public download(downloads: any) {
        this.downloadHelper.downloadWithOptions(downloads);
    }
    browse(resource: Resource) {
        this.router.navigate(['/home/dashboard/resource-details/' + resource.id]);
    }

    getfirstColor(enrollmentCounts: {
        approved: number;
        cancelled: number;
        pending: number;
        rejected: number;
        total: number;
        total_seats: number | null;
        waiting: number;
    }) {
        if (enrollmentCounts.total === enrollmentCounts.approved) return 'grey-on-green';
        if (enrollmentCounts.total > enrollmentCounts.approved) return 'white-on-orange';
        if (enrollmentCounts.approved < enrollmentCounts.total_seats) return 'dark-blue-on-green';
        if (enrollmentCounts.total_seats < enrollmentCounts.total) return 'orange-on-white';
    }
    getsecondColor(enrollmentCounts: {
        approved: number;
        cancelled: number;
        pending: number;
        rejected: number;
        total: number;
        total_seats: number | null;
        waiting: number;
    }) {
        if (enrollmentCounts.total === enrollmentCounts.approved) return 'grey-on-green';
        if (enrollmentCounts.total > enrollmentCounts.approved) return 'white-on-orange';
    }
    getEnrollToolTip(enrollmentCounts: {
        approved: number;
        cancelled: number;
        pending: number;
        rejected: number;
        total: number;
        total_seats: number | null;
        waiting: number;
    }) {
        return [
            `${$localize`Approved`}: ${enrollmentCounts.approved.toString()}`,
            `${$localize`Cancelled`}: ${enrollmentCounts.cancelled.toString()}`,
            `${$localize`Pending`}: ${enrollmentCounts.pending.toString()}`,
            `${$localize`Rejected`}: ${enrollmentCounts.rejected.toString()}`,
            `${$localize`Total Enrollments`}: ${enrollmentCounts.total.toString()}`,
            `${$localize`Waiting`}: ${enrollmentCounts.waiting.toString()}`,
            `${$localize`Total Seats`}: ${
                enrollmentCounts.total_seats === null ? $localize`Unlimited` : enrollmentCounts.total_seats.toString()
            }`,
        ].join('\n');
    }
    getTotalSeatsToolTip(enrollmentCounts: {
        approved: number;
        cancelled: number;
        pending: number;
        rejected: number;
        total: number;
        total_seats: number | null;
        waiting: number;
    }) {
        return `${$localize`Total seats`}: ${
            enrollmentCounts.total_seats === null ? $localize`Unlimited` : enrollmentCounts.total_seats.toString()
        }`;
    }
    getOpportunityAuthorTooltip(resource: Resource) {
        if (!resource.opportunity_author?.id) return;
        return $localize`${resource.opportunity_author.fname} ${resource.opportunity_author.lname} has marked you as a participant`;
    }
    canExpand(resource: Resource) {
        return !!resource.active_occasion_dates?.length;
    }
    get isCompletedFilter(): boolean {
        return (
            !!this.tableFilterItems?.length && !!this.tableFilterItems.filter((fltr) => fltr.property == 'done').length
        );
    }
    addOccasion(resource: Resource) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'more_time',
                title: $localize`Add occasion`,
                contentBody: $localize`You are about to add occasion to ${resource.title}.`,
                positiveBtn: $localize`Done`,
                injectedTemplate: this.occasionsManager,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                takeUntil(this.destroy),
                switchMap(() => {
                    const state = this.latestOccasionFormState;
                    if (state?.valid && state.value) {
                        return this.occasionsDatesService
                            .createOccasionDate(resource.id, state.value)
                            .pipe(
                                tap(() =>
                                    this.messageService.openMessage(
                                        MessageSavedComponent,
                                        $localize`Occasion added successfully.`
                                    )
                                )
                            );
                    }
                    this.messageService.openMessage(
                        MessageErrorComponent,
                        $localize`Please fill all the fields correctly.`
                    );
                    return EMPTY;
                })
            )
            .subscribe((data) => {
                if (data) {
                    this.onChangeStatus();
                }
            });
    }
    isActivityTab(): boolean {
        return (
            !!this.tableFilterItems?.length &&
            !!this.tableFilterItems.filter((fltr) => fltr.property == 'userActivity').length
        );
    }
    public deleteCourseFromList(resource: Resource) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'delete',
                title: $localize`Delete ${resource.title}`,
                contentBody: $localize`You are about to delete ${resource.title} from being displayed in the activities. Please use caution and make sure that you really intend to delete before confirming delete below.`,
                positiveBtn: $localize`Delete`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                takeUntil(this.destroy),
                filter((res) => !!res),
                switchMap(() => this.resourceOpportunityService.destroy(resource.opportunity_id))
            )
            .subscribe({
                next: () => {
                    this.tableBase.dataSource.removeById(resource.id);
                    this.messageService.openMessage(
                        MessageSavedComponent,
                        $localize`:@@resoureHasBeenHidden:The resource has been successfully deleted from the list.`
                    );
                },
                error: () => {
                    this.messageService.openMessage(
                        MessageErrorComponent,
                        $localize`:@@someThingWentWrong:Oops! Something went wrong. Please try again later.`
                    );
                },
            });
    }
    public downloadSelected(res: Resource[]) {
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.DOWNLOAD,
            data: {
                data: Utils.getDownloadOptions('resource_opportunity'),
                returnValue: true,
            },
        });
        this.mobile
            .getSideNavAction()
            .pipe(
                filter((action) => action === SideNaveActionsTypes.POSITIVE),
                switchMap(() => this.mobile.getSideNavResponseData()),
                takeUntil(this.destroy)
            )
            .subscribe((result) => {
                const ids = res.map((res) => res.opportunity_id);
                if (!Utils.isEmpty(result?.value) && !Utils.isEmpty(ids)) {
                    this.oppoService.download(ids, result.value.join()).subscribe((res) => {
                        const file = new Blob([res], { type: res.type });
                        const fileUrl = URL.createObjectURL(file);
                        const anchor = document.createElement('a');
                        anchor.href = fileUrl;
                        anchor.download = `KLMS_${moment().format('yMD_hms')}`;
                        anchor.click();
                        this.tableBase.selection.clear();
                    });
                }
            });
    }
    public play(resource: Resource, playableResourceCheck?: any): void {
        this.resourceActionHandlerService
            .playResource({ resource, resPlayableSpec: playableResourceCheck })
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res) {
                    this.tableBase.dataSource.refresh();
                }
            });
        // if (playableResourceCheck && !playableResourceCheck.clickable) {
        //     this.displayNotAllowedMsg(playableResourceCheck.desc);
        //     return;
        // }
        // this.startResourceService.takeResource(resource, this.user)?.subscribe();
    }
    public playFromOccasion(params: {
        occasion: OccasionDate;
        resource: Resource;
        playableResourceCheck: PlayableResourceSpec;
    }): void {
        this.resourceActionHandlerService
            .playResource({
                resource: params.resource,
                resPlayableSpec: params.playableResourceCheck,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res) {
                    this.tableBase.dataSource.refresh();
                }
            });
    }
    public displayNotAllowedMsg(msg: string) {
        this.messageService.openMessage(MessageErrorComponent, msg);
    }

    canEdit(resource: Resource, customer: Customer) {
        return (
            !!resource.user_collaboration?.edit?.status &&
            (this.user.isAdmin() ||
                customer.authoring_allowed_types?.includes(AppScope.RESOURCES) ||
                customer.authoring_allowed_types?.includes(ResourceTypes.scopeByChildType(resource.type_id)))
        );
    }

    edit(resource: Resource) {
        if (resource.type_id === ResourceTypes.GeneralCoursePlan) {
            this.user.isAdmin()
                ? this.router.navigate(['/admin/content/courses/', resource.id])
                : this.router.navigate(['/home/manage/course-plan/', resource.id]);
        } else {
            this.user.isAdmin()
                ? this.router.navigate([`/admin/content/${ResourceTypes.playerRoute(resource.type_id)}s/`, resource.id])
                : this.router.navigate([`/home/manage/${ResourceTypes.playerRoute(resource.type_id)}/`, resource.id]);
        }
    }
    onEditCreated(resource: Resource, customer: Customer) {
        if (resource.occasionStatus == 'created' && this.canEdit(resource, customer)) {
            this.edit(resource);
        }
    }
    expansionToggled(resource: Resource) {
        if (this.canExpand(resource)) {
            const expandId = resource.opportunity_id || resource.id;
            if (this.expandedRowIds) {
                if (this.expandedRowIds.has(expandId)) {
                    this.expandedRowIds.delete(expandId);
                    this.expandedRowIds = new Set(this.expandedRowIds);
                } else {
                    this.expandedRowIds = new Set([...this.expandedRowIds, expandId]);
                }
            } else {
                this.expandedRowId = this.expandedRowId === expandId ? null : expandId;
            }
            this.cdr.markForCheck();
        }
    }

    onExpandAllClicked(resources: Resource[]): void {
        const expandableResources = resources.filter((r) => this.canExpand(r));
        const expandableIds = expandableResources.map((r) => r.opportunity_id || r.id);
        this.expandedRowIds = new Set(expandableIds);
        this.cdr.markForCheck();
    }

    onCollapseAllClicked(): void {
        this.expandedRowIds = null;
        this.expandedRowId = null;
        this.cdr.markForCheck();
    }
}
